import {
  type DataTableV2ColumnThreshold,
  type DataTableV2RowThreshold,
} from './threshold-types.js';
import { makeAccessorFunction } from '../../hooks/makeAccessorFunction.js';
import type { DataTableV2RowData } from '../../public.api.js';

/**
 * @internal
 * Internal normalized threshold rules.
 */
export interface DataTableV2ThresholdsNormalizedRule<
  TData extends DataTableV2RowData,
> {
  /**
   * Text or pill color for the threshold
   */
  color?: string;
  /**
   * Background color for the cell
   */
  backgroundColor?: string;
  /**
   * Threshold rules array.
   */
  rules: Array<{
    /**
     * Accessor function that, given the rowData returns a value that can be compared against.
     */
    accessor?: (row: TData) => unknown;
    /** Comparison value of a given rule. */
    value?: unknown;
    /** Simple comparator definition or function for a threshold comparison. */
    comparator:
      | (
          | 'greater-than'
          | 'less-than'
          | 'greater-than-or-equal-to'
          | 'less-than-or-equal-to'
          | 'equal-to'
          | 'not-equal-to'
        )
      | ((row: TData) => boolean);
  }>;
  /**
   * Optional part for the row highlight types.
   */
  type?: 'pill' | 'highlight';
}

/**
 * @internal
 * Helper function that will normalize the given accessor to a function
 * in the normalized threshold rules.
 */
function makeAccessor<TData extends DataTableV2RowData>(
  accessor?: string | ((row: TData) => unknown),
  fallbackAccessor?: string | ((row: TData) => unknown),
): (row: TData) => unknown {
  // Check if the accessor is defined. If it is defined use this one for
  // the checks. If not, use the one from the columnDef.
  const appliedAccessor = accessor ?? fallbackAccessor;

  if (typeof appliedAccessor === 'string') {
    // Check if the string access contains JSONPath like characters, that would
    // require a complex replacer logic.
    if (appliedAccessor.includes('.') || appliedAccessor.includes('[')) {
      return makeAccessorFunction(appliedAccessor);
    } else {
      return (row: TData) => {
        // We can no longer infer this in the callback
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (row as any)[appliedAccessor];
      };
    }
  } else if (typeof appliedAccessor === 'function') {
    return appliedAccessor;
  }
  return () => undefined;
}

/**
 * Normalizes the simple rules and combined rules into a standard internal
 * rule set.
 * @internal
 */
export function normalizeThresholdRules<
  TData extends DataTableV2RowData,
  ThresholdRule extends
    | DataTableV2ColumnThreshold<TData>[]
    | DataTableV2RowThreshold<TData>[],
>(
  thresholds: ThresholdRule,
  fallbackAccessor?: string | ((row: TData) => unknown),
): Array<DataTableV2ThresholdsNormalizedRule<TData>> {
  // Unify the rules into a single setup in order to combine simple and combined rules:
  const columnThresholdDefinitions: Array<
    DataTableV2ThresholdsNormalizedRule<TData>
  > = [];

  for (const threshold of thresholds) {
    // Create the basis of the normalized rule.
    const unifiedRule: DataTableV2ThresholdsNormalizedRule<TData> = {
      color: threshold.color,
      backgroundColor:
        'backgroundColor' in threshold ? threshold.backgroundColor : undefined,
      rules: [],
      type: 'type' in threshold ? threshold.type : undefined,
    };

    // Map the simple rules to an array as well, so we do not need to deal
    // with two different datatypes further down the line.
    const normalizedRulesArray =
      threshold.rules !== undefined
        ? threshold.rules
        : [
            {
              accessor:
                'accessor' in threshold ? threshold.accessor : undefined,
              value: 'value' in threshold ? threshold.value : undefined,
              comparator: threshold.comparator,
            },
          ];

    unifiedRule.rules = normalizedRulesArray.map((rule) => {
      return {
        // Accessors might be undefined in scenarios where a custom comparator function
        // is given.
        accessor:
          'accessor' in rule
            ? makeAccessor(rule.accessor, fallbackAccessor)
            : undefined,
        // Value might be undefined in scenarios where a custom comparator function
        // is given.
        value: 'value' in rule ? rule.value : undefined,
        comparator: rule.comparator,
      };
    });

    columnThresholdDefinitions.push(unifiedRule);
  }
  return columnThresholdDefinitions;
}
